import { useEffect, useRef } from 'react';
import { useQuote } from '../../store/selectors/useQuote';
import {
  CheckoutFailure,
  CheckoutPartialSuccess,
  CheckoutResponseDetail,
  CheckoutSuccess,
  CheckoutTimeout,
} from './CheckoutResponses';
import { usePostHog } from '@posthog/react';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import HeroImage from './CheckoutHero.avif';
import { getSystemCode } from '../../utils/getSystemCode';
import { StepKey } from '../../enums/stepKey';
import { FormKey } from '../../enums/formKey';
import { log } from '@domgen/shared/utils/log';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { store } from '../../store/store';
import { useApplianceFormData } from '../../store/selectors/useApplianceFormData';
import { useAppState } from '../../store/selectors/useAppState';
import { useCheckout } from '../../store/selectors/useCheckout';
import { useGaEventData } from '../../store/selectors/useGaEventData';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { formApplianceName } from '../../utils/store/formApplianceName';
import { formCategoryName } from '../../utils/store/formCategoryName';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { StoreEvent } from '../../enums/storeEvent';
import { isReplacementCare } from '../../utils/store/isReplacementCare';
import { useStoreContext } from '../../store/selectors/useStoreContext';

export function CheckoutPage() {
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const formData = useApplianceFormData();
  const systemCode = getSystemCode(urlParams?.rs);
  const quote = useQuote();
  const checkout = useCheckout();
  const posthog = usePostHog();
  const element = useRef<HTMLElement | null>(null);
  const getElementRef = (el: HTMLElement | null) => {
    element.current = el;
  };
  const { VITE_CLIENT_FRIENDLY_NAME } = import.meta.env;
  const gaEventData = useGaEventData() || {};
  const offerPrice = gaEventData?.netFirstPaymentAmount;
  const salesWidgetClicked = gaEventData?.salesWidgetClicked;
  const applianceName = formApplianceName.get();
  const applianceCategory = formCategoryName.get();
  const storeContext = useStoreContext();
  const hasReplacementCare = isReplacementCare(storeContext);
  const pageName = `olr-registration-payment-details${hasReplacementCare ? '-replacement-care' : ''}`;
  const renderCount = useRef(0);
  renderCount.current += 1;

  useEffect(() => {
    gaPageView({
      event: 'spa-pageview',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      appliancePurchaseCost: formData?.[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
      appliancePurchaseDate: formatLocalisedDate(
        parseLocalDateString(formData?.[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] || ''),
        'en-US',
      ),
      modelNumberDetail: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
      serialNumberDetail: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER],
      serialNumber: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER]
        ? 'provided'
        : 'not provided',
      modelNumber: formData?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER]
        ? 'provided'
        : 'not provided',
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
    });
  }, []);

  const handleCheckoutEvent = <K extends Event>() => ({
    handleEvent: (checkoutResponse: K & { detail: CheckoutResponseDetail }) => {
      capturePostHogEvent(posthog, 'checkout-terms-clicked', storeContext);
      capturePostHogEvent(posthog, 'checkout-setup-protection-clicked', storeContext);
      switch (checkoutResponse.detail.status) {
        case 'success':
        case 'partial_success':
          store.send({
            type: StoreEvent.CHECKOUT_SUCCESS,
            data: checkoutResponse.detail as CheckoutSuccess,
          });
          handleSetupProtection();
          break;
        case 'timeout':
          log.debug({ message: 'The contracts creation timed out', data: checkoutResponse.detail });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
        case 'error':
          log.debug({
            message: 'There was an error with payment or contract creation',
            data: checkoutResponse.detail,
          });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
        default:
          log.debug({
            message: 'Something went wrong, navigating to error page',
            data: checkoutResponse.detail,
          });
          store.send({ type: StoreEvent.CHECKOUT_FAILED });
          break;
      }
    },
  });

  const handleSetupProtection = () => {
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'Setup my protection',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'genericGAEvent',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      quoteId: quote?.data?.offer?.offerReference,
    });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'I agree to terms of use - checkbox confirmed',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'genericGAEvent',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
    });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'paymentDetails',
      eventLabel: 'automatic renewal - checkbox confirmed',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      offerPrice,
      salesWidgetClicked,
      quoteId: quote?.data?.offer?.offerReference,
    });
  };

  useEffect(() => {
    if (checkout?.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__loadCheckoutCount = ((window as any).__loadCheckoutCount ?? 0) + 1;
      posthog.capture('checkout-sdk-loaded', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        loadCheckoutCount: (window as any).__loadCheckoutCount,
        pageRenderCount: renderCount.current,
        targetPresent: !!document.getElementById('checkout-panel'),
        readyState: document.readyState,
      });
      window.dngCheckout.loadCheckout({
        targetElementId: 'checkout-panel',
        ...checkout?.data,
      });
    }
  }, [checkout]);

  useEffect(() => {
    const checkoutSuccessHandler = handleCheckoutEvent<CheckoutSuccess>();
    const checkoutTimeoutHandler = handleCheckoutEvent<CheckoutTimeout>();
    const checkoutFailureHandler = handleCheckoutEvent<CheckoutFailure>();
    const checkoutPartialSuccessHandler = handleCheckoutEvent<CheckoutPartialSuccess>();

    window.addEventListener('dngCheckoutSuccess', checkoutSuccessHandler);
    window.addEventListener('dngCheckoutTimeout', checkoutTimeoutHandler);
    window.addEventListener('dngCheckoutFailure', checkoutFailureHandler);
    window.addEventListener('dngCheckoutPartialSuccess', checkoutPartialSuccessHandler);

    return () => {
      window.removeEventListener('dngCheckoutSuccess', checkoutSuccessHandler);
      window.removeEventListener('dngCheckoutTimeout', checkoutTimeoutHandler);
      window.removeEventListener('dngCheckoutFailure', checkoutFailureHandler);
      window.removeEventListener('dngCheckoutPartialSuccess', checkoutPartialSuccessHandler);
    };
  }, []);

  return (
    <div className="bg-neutral-100">
      <div className="relative w-full h-50 overflow-hidden">
        <img
          src={HeroImage}
          alt=""
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-360 h-auto max-w-none"
        />
        <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="bg-bg-main-container p-6 rounded max-w-205 text-center">
            <h1 className="text-h3">
              {checkout?.data.customer.firstName}, let's set up your {VITE_CLIENT_FRIENDLY_NAME}{' '}
              {hasReplacementCare ? 'Replacement Care' : 'Care Plus'} plan
            </h1>
          </div>
        </div>
      </div>
      <div className="col-span-full">
        <div
          id="checkout-panel"
          className="pt-10 pb-6 min-h-375 bg-neutral-100"
          ref={getElementRef}
        ></div>
      </div>
    </div>
  );
}
